// Day view, the home screen for admins and viewers (SPEC §7.2): one center's classrooms as a grid that fits the
// screen, or "All centers" as a dense grid (default) or a list, with what needs attention first.

import type { ApiResult } from "../../api/client";
import { fetchCenters } from "../../api/centers";
import { fetchCenterDay, fetchSchoolDay } from "../../api/views";
import type { Center, CenterDay, ClassroomColumn, SchoolDay } from "../../api/types";
import { t, translateCode } from "../../i18n/i18n";
import { getLastCenterId, setLastCenterId } from "../../preferences";
import { isCurrentPage, navigate, setRedraw, type RouteMatch } from "../../router";
import { requireSchool } from "../../session";
import { buildDateNavigator } from "../../ui/dateNavigator";
import { createButton, createElement } from "../../ui/dom";
import { createSelectField, type Field } from "../../ui/forms";
import { classroomToColumn } from "../../ui/gridColumns";
import { buildTimeline, fitTimelinesToScreen, type TimelineColumn } from "../../ui/timeline";
import { createLoading, showPageProblem, startPage } from "../pageFrame";
import { buildAllCentersDay, type DayLayout } from "./allCentersView";

/** The value of the center dropdown that means every center. */
export const ALL_CENTERS: string = "all";

interface DaySelection {
  centers: Center[];
  centerId: string;
  date: string;
  layout: DayLayout;
}

type DayData = { kind: "center"; day: CenterDay } | { kind: "all"; day: SchoolDay };

export async function renderDayPage(container: HTMLElement, route: RouteMatch): Promise<void> {
  const page: HTMLElement = startPage(container, t("day.title"));
  page.append(createLoading());
  const centers: ApiResult<Center[]> = await fetchCenters(false);
  if (centers.ok === false) {
    showPageProblem(page, centers.problem);
    return;
  }

  const centerId: string | null = chooseCenter(centers.data, route.query.get("center"));
  if (centerId === null) {
    page.replaceChildren(createElement("h1", "", t("day.title")), createElement("p", "", t("day.noCenters")));
    return;
  }

  const selection: DaySelection = {
    centers: centers.data,
    centerId,
    date: route.query.get("date") ?? requireSchool().today,
    layout: route.query.get("view") === "list" ? "list" : "grid",
  };
  await loadAndDraw(container, page, selection);
}

async function loadAndDraw(container: HTMLElement, page: HTMLElement, selection: DaySelection): Promise<void> {
  const data: ApiResult<DayData> = await loadDay(selection);
  if (isCurrentPage(container) === false) {
    return; // The user moved on (another date or center) while this loaded; that page draws itself.
  }

  if (data.ok === false) {
    showPageProblem(page, data.problem);
    return;
  }

  const draw = (): void => {
    const freshPage: HTMLElement = startPage(container, t("day.title"));
    freshPage.append(buildToolbar(selection), ...buildDayBody(data.data, selection));
    fitTimelinesToScreen();
  };
  setRedraw(container, draw);
  draw();
}

async function loadDay(selection: DaySelection): Promise<ApiResult<DayData>> {
  if (selection.centerId === ALL_CENTERS) {
    const day: ApiResult<SchoolDay> = await fetchSchoolDay(selection.date);
    return day.ok ? { ok: true, data: { kind: "all", day: day.data } } : day;
  }

  // Only a single center is remembered: opening the Day view never starts on "All centers" (product decision).
  setLastCenterId(selection.centerId);
  const day: ApiResult<CenterDay> = await fetchCenterDay(selection.centerId, selection.date);
  return day.ok ? { ok: true, data: { kind: "center", day: day.data } } : day;
}

/** The requested center (or "all"), else the last one used in this browser, else the first. */
export function chooseCenter(centers: Center[], requested: string | null): string | null {
  if (requested === ALL_CENTERS && centers.length > 0) {
    return ALL_CENTERS;
  }

  const candidates: (string | null)[] = [requested, getLastCenterId()];
  for (const candidate of candidates) {
    if (centers.some((center: Center): boolean => center.id === candidate)) {
      return candidate;
    }
  }

  return centers[0]?.id ?? null;
}

/** "/day?center=…&date=…&view=…" — every choice is in the URL so it can be bookmarked (SPEC §9.4). */
export function buildDayPath(selection: { centerId: string; date: string; layout: DayLayout }): string {
  const query: URLSearchParams = new URLSearchParams({ center: selection.centerId, date: selection.date });
  if (selection.centerId === ALL_CENTERS && selection.layout === "list") {
    query.set("view", "list");
  }

  return `/day?${query.toString()}`;
}

function buildToolbar(selection: DaySelection): HTMLElement {
  const options: { value: string; label: string }[] = [{ value: ALL_CENTERS, label: t("day.allCenters") },
    ...selection.centers.map((center: Center): { value: string; label: string } => ({
      value: center.id, label: center.name }))];
  const centerField: Field<HTMLSelectElement> = createSelectField(t("day.center"), options, selection.centerId);
  centerField.input.addEventListener("change", (): void => {
    navigate(buildDayPath({ ...selection, centerId: centerField.input.value }));
  });
  const toolbar: HTMLDivElement = createElement("div", "toolbar day-toolbar");
  toolbar.append(centerField.wrapper);
  if (selection.centerId === ALL_CENTERS) {
    toolbar.append(buildLayoutSwitch(selection));
  }

  toolbar.append(buildDateNavigator(selection.date, 1, (date: string): void => {
    navigate(buildDayPath({ ...selection, date }));
  }));
  return toolbar;
}

/** Grid (default) / List for "All centers". */
function buildLayoutSwitch(selection: DaySelection): HTMLElement {
  const group: HTMLDivElement = createElement("div", "segmented");
  group.setAttribute("role", "group");
  group.setAttribute("aria-label", t("day.view"));
  for (const layout of ["grid", "list"] as const) {
    const button: HTMLButtonElement = createButton(layout === "grid" ? t("day.viewGrid") : t("day.viewList"),
      (): void => {
        navigate(buildDayPath({ ...selection, layout }));
      });
    button.setAttribute("aria-pressed", String(selection.layout === layout));
    group.append(button);
  }

  return group;
}

function buildDayBody(data: DayData, selection: DaySelection): HTMLElement[] {
  if (data.kind === "all") {
    return buildAllCentersDay(data.day, selection.layout);
  }

  return [...buildClosureBanner(data.day), buildCenterGrid(data.day)];
}

function buildClosureBanner(day: CenterDay): HTMLElement[] {
  if (day.closure === null) {
    return [];
  }

  const reason: string = translateCode("closureReasons", day.closure.reason);
  return [createElement("div", "banner", t("day.closed", { reason }))];
}

/** One center's classrooms, fitted to the screen (SPEC §9.3). */
function buildCenterGrid(day: CenterDay): HTMLElement {
  if (day.columns.length === 0) {
    return createElement("p", "muted", t("day.noClassrooms"));
  }

  const columns: TimelineColumn[] = day.columns.map((column: ClassroomColumn): TimelineColumn =>
    classroomToColumn(column, undefined));
  return buildTimeline(columns, "fit");
}
